import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { AutomationRun, AutomationWorkflow } from '@/types';
import { cn, statusColors } from '@/lib/utils';

export interface CampaignPublishPanelProps {
  automationLoading: boolean;
  automationWorkflows: AutomationWorkflow[];
  automationRuns: AutomationRun[];
  scheduleWorkflowId: string;
  scheduleAt: string;
  onScheduleWorkflowIdChange: (id: string) => void;
  onScheduleAtChange: (value: string) => void;
  onPublishAll: (dryRun?: boolean) => void;
  onSchedule: () => void;
}

/** Campaign detail “Automatisierung” tab: publish-all, schedule, recent runs. */
export function CampaignPublishPanel({
  automationLoading,
  automationWorkflows,
  automationRuns,
  scheduleWorkflowId,
  scheduleAt,
  onScheduleWorkflowIdChange,
  onScheduleAtChange,
  onPublishAll,
  onSchedule,
}: CampaignPublishPanelProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Automatisierung</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => void onPublishAll(false)}>Jetzt publizieren</Button>
          <Button variant="outline" onClick={() => void onPublishAll(true)}>
            Dry Run
          </Button>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div className="grid gap-2 sm:col-span-2">
            <Label>Workflow</Label>
            <Select value={scheduleWorkflowId} onValueChange={onScheduleWorkflowIdChange}>
              <SelectTrigger>
                <SelectValue placeholder="Workflow wählen" />
              </SelectTrigger>
              <SelectContent>
                {automationWorkflows.map((workflow) => (
                  <SelectItem key={workflow.id} value={workflow.id}>
                    {workflow.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Zeitpunkt</Label>
            <Input
              type="datetime-local"
              value={scheduleAt}
              onChange={(event) => onScheduleAtChange(event.target.value)}
            />
          </div>
        </div>
        <div>
          <Button variant="outline" onClick={() => void onSchedule()}>
            Planung speichern
          </Button>
        </div>

        <div>
          <h4 className="mb-2 text-sm font-medium">Letzte Runs</h4>
          {automationLoading ? (
            <p className="text-sm text-muted">Automationsstatus wird geladen…</p>
          ) : automationRuns.length === 0 ? (
            <p className="text-sm text-muted">Noch keine Runs vorhanden.</p>
          ) : (
            <div className="space-y-2">
              {automationRuns.map((run) => (
                <div key={run.id} className="rounded-lg border border-border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm">{run.trigger_source}</p>
                    <Badge className={cn(statusColors[run.status])}>{run.status}</Badge>
                  </div>
                  {run.error_message && (
                    <p className="mt-1 text-xs text-danger">{run.error_message}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
